import type {
	CoordinatesInput,
	Currency$options,
	DistanceSystem$options,
	ItineraryConstraint$options,
	ItineraryTrafficMode$options,
	ItineraryTravelMode$options,
} from '$houdini';

import {
	currenciesList,
	distanceSystemsList,
	itineraryTrafficMap,
	travelModesList,
	tripConstraintsList,
} from '$lib/components/itinerary/constant';
import { ssp } from 'sveltekit-search-params';

interface SearchParams {
	coordinates: CoordinatesInput[];
	carId: string;
	constraint: ItineraryConstraint$options | null;
	currency: Currency$options | null;
	distanceSystem: DistanceSystem$options | null;
	energyCost: number | null;
	initialChargeLevel: number | null;
	mode: ItineraryTravelMode$options | null;
	rechargeTreshold: number | null;
	arrivalName: string;
	departureName: string;
	selectedRoute: number;
	withCaravan: boolean;
	traffic: ItineraryTrafficMode$options | null;
}

interface SearchParamOption<T = any> {
	encode: (value: T) => string;
	decode: (value: string | null) => T | null;
}

type SearchParamsRecord = Record<keyof SearchParams, SearchParamOption>;

const searchParamsOptions: SearchParamsRecord = {
	coordinates: {
		encode(value: CoordinatesInput[]) {
			return value ? JSON.stringify(value) : '';
		},
		decode(value: string | null) {
			return value ? JSON.parse(value) : null;
		},
	},
	carId: ssp.string(),
	energyCost: ssp.number(),
	initialChargeLevel: ssp.number(),
	mode: {
		...ssp.string(),
		decode(str) {
			if (!travelModesList.includes(str as ItineraryTravelMode$options)) {
				return null;
			}
			return str as ItineraryTravelMode$options;
		},
	},
	constraint: {
		...ssp.string(),
		decode(str) {
			if (!tripConstraintsList.includes(str as ItineraryConstraint$options)) {
				return null;
			}
			return str as ItineraryConstraint$options;
		},
	},
	distanceSystem: {
		...ssp.string(),
		decode(str) {
			if (!distanceSystemsList.includes(str as DistanceSystem$options)) {
				return null;
			}
			return str as DistanceSystem$options;
		},
	},
	currency: {
		...ssp.string(),
		decode(str) {
			if (!currenciesList.includes(str as Currency$options)) {
				return null;
			}
			return str as Currency$options;
		},
	},
	selectedRoute: ssp.number(),
	rechargeTreshold: { ...ssp.number() },
	arrivalName: ssp.string(),
	departureName: ssp.string(),
	withCaravan: ssp.boolean(),
	traffic: {
		...ssp.string(),
		decode(str) {
			return str ? itineraryTrafficMap[str] || null : null;
		},
	},
};

export function parseRoadsheetMobileSearchParams(usp: URLSearchParams): SearchParams {
	return {
		...Object.fromEntries(
			[...usp.entries()].map(([key, value]) => {
				// @ts-expect-error untyped access
				const paramsOption = searchParamsOptions[key];
				if (paramsOption) {
					return [key, paramsOption.decode(value) ?? paramsOption.defaultValue];
				}
				return [key, value];
			}),
		),
	} as SearchParams;
}
